import { Hook } from "@/components/Hook";
import { hasImportedBeforeSelector } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

import {
  ClubEmptyStateContext,
  IMPORT_CTA_LABEL,
  clubEmptyStateCopy,
} from "./emptyClubCopy";
import { useStartClubImport } from "./useStartClubImport";

type Props = {
  /** Which list came back empty. Picks the copy and the tracking coordinates. */
  context: ClubEmptyStateContext;
  /**
   * The surface this empty state is rendered on ("Players", "My Club", ...).
   * The component appears on several, and the CTA starts a signup flow that
   * loses its origin without it — see clubImportPrompt.noteClubImportIntent.
   */
  section: string;
};

/**
 * Inline empty state for every list that is filtered to club players and came
 * back with none. Same shape as the My Evolutions empty state: title, subtitle,
 * one import CTA.
 *
 * First-timers get the copy without the button. Every surface that can show
 * this to someone who has never imported already leads with an import pitch of
 * its own, and a second CTA a screen below the first reads as clutter rather
 * than as help. A returning user whose club came back empty has no such pitch
 * above them, so they keep the button.
 */
export const ClubEmptyState = ({ context, section }: Props) => {
  const { t } = useTranslation("myClub");
  const hasImportedBefore = useSelector(hasImportedBeforeSelector);
  const { title, subtitle } = clubEmptyStateCopy(context, hasImportedBefore);
  // `context` already names the list precisely ("active-squad", "sbc-fodder"),
  // so it is the subsection rather than inventing a second vocabulary.
  const startClubImport = useStartClubImport({ section, subsection: context });

  return (
    <div className="mt-6 w-full overflow-hidden rounded-lg">
      <Hook
        title={t(title)}
        subtitle={t(subtitle)}
        cta={
          hasImportedBefore
            ? { label: t(IMPORT_CTA_LABEL), onClick: startClubImport }
            : undefined
        }
      />
    </div>
  );
};
